import { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { getBeatsPerBar } from '../../../utils/songTiming';

// Where a numeral's centre is, and how big it is drawn
const measureNumeral = (element) => {
  const box = element.getBoundingClientRect();

  return {
    x: box.left + box.width / 2,
    y: box.top + box.height / 2,
    size: parseFloat(getComputedStyle(element).fontSize),
  };
};

// The ring round the corner count, in its own viewBox units, and how much of each beat's arc is
// left as the gap before the next
const RING_SIZE = 48;
const RING_RADIUS = 21;
const RING_GAP = 0.3;

/**
 * A ring of one arc per beat of the bar, the beat being played lit — so the corner count says where
 * in the bar it is as well as which beat, in one place rather than a second moving part.
 */
const BeatRing = ({ count, beat }) => {
  const centre = RING_SIZE / 2;
  const circumference = 2 * Math.PI * RING_RADIUS;
  const segment = circumference / count;
  const arc = segment * (1 - RING_GAP);

  return (
    <svg className="beat-ring" viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <circle
          key={i}
          className={`beat-arc${i === beat - 1 ? ' beat-arc--on' : ''}`}
          cx={centre}
          cy={centre}
          r={RING_RADIUS}
          strokeDasharray={`${arc} ${circumference - arc}`}
          // Each arc centred on its own share of the ring, the first starting at the top
          strokeDashoffset={-(i * segment + (segment - arc) / 2)}
          transform={`rotate(-90 ${centre} ${centre})`}
        />
      ))}
    </svg>
  );
};

BeatRing.propTypes = {
  count: PropTypes.number.isRequired,
  beat: PropTypes.number.isRequired,
};

/**
 * The count, drawn over whatever the chart is being followed through: one part's words, or a run
 * of parts.
 *
 * The count-in bar is drawn rather than only heard — the content dims and the beat is put on the
 * screen large. This is the view someone sings from, and the bar before the chart is the one where
 * they find the beat and take a breath; a click on a phone, in a room with other people playing, is
 * easy to miss. It lasts exactly that bar: countInBeat goes to 0 with the first chord and the words
 * come back to full. A chart that comes in during the count is `entered` partway through it: the
 * words come back then, and the numeral finishes the bar over them.
 *
 * The count doesn't end there: the numeral shrinks into the top-right corner and keeps the beat of
 * the bar for as long as the chart plays, so the timing stays in sight while the words are read.
 */
const CountedView = ({ countInBeat, beat, timeSignature, entered = false, children }) => {
  const counting = countInBeat > 0;
  const keeping = beat > 0 && !counting;

  const countRef = useRef(null);
  const beatRef = useRef(null);
  // The count-in numeral as of its last click — it is gone by the time the corner one mounts
  const handoffRef = useRef(null);

  // Flown in from where the count-in was (FLIP), rather than simply appearing, so it reads as the
  // same count carrying on. Declared ahead of the effect below so it reads the handoff before
  // that clears it.
  useLayoutEffect(() => {
    const from = handoffRef.current;
    const element = beatRef.current;

    if (!keeping || !from || !element) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const to = measureNumeral(element);

    element.animate(
      {
        transform: [`translate(${from.x - to.x}px, ${from.y - to.y}px) scale(${from.size / to.size})`, 'none'],
        backgroundColor: ['transparent', getComputedStyle(element).backgroundColor],
      },
      { duration: 450, easing: 'cubic-bezier(0.3, 0, 0.2, 1)' },
    );
  }, [keeping]);

  useLayoutEffect(() => {
    handoffRef.current = counting && countRef.current ? measureNumeral(countRef.current) : null;
  }, [counting, countInBeat]);

  return (
    <div className="lyrics-view">
      {/* Sticky so the corner count stays on screen as the dialog scrolls down the words */}
      <div className="beat-layer" aria-hidden="true">
        {keeping && (
          <span className="beat" ref={beatRef}>
            <BeatRing count={getBeatsPerBar(timeSignature)} beat={beat} />
            {/* Keyed on the beat, like the count-in, so each click strikes afresh */}
            <span key={beat}>{beat}</span>
          </span>
        )}
      </div>
      <div className={cx('counted', { 'counted--counting': counting && !entered })}>{children}</div>
      {/* Keyed on the beat so each click mounts a numeral of its own, which is what starts its
          animation over. aria-hidden: the count is already being heard. */}
      {counting && (
        <span className="count-in" key={countInBeat} ref={countRef} aria-hidden="true">
          {countInBeat}
        </span>
      )}
    </div>
  );
};

CountedView.propTypes = {
  countInBeat: PropTypes.number.isRequired,
  beat: PropTypes.number.isRequired,
  timeSignature: PropTypes.string.isRequired,
  // A chord has struck — before the count-in is over, where the chart comes in during it
  entered: PropTypes.bool,
  children: PropTypes.node,
};

export default CountedView;
